import { matchPath } from 'react-router-dom';
import type { SearchDomain } from '@shared/types';

type ShellActionId = 'notification' | 'create' | 'delete' | 'sync';
type DeleteDomain = Extract<
  SearchDomain,
  'projects' | 'leads' | 'tasks' | 'customers' | 'resources' | 'contracts' | 'invoices' | 'payments'
>;

interface ShellRouteDefinition {
  pattern: string;
  labelKey: string;
  mode: 'static' | 'collection' | 'editor';
  parentPath?: string;
  parentLabelKey?: string;
  detailParam?: string;
  detailFallbackKey?: string;
  deleteDomain?: DeleteDomain;
  actionIds: ShellActionId[];
}

export interface ShellBreadcrumb {
  labelKey?: string;
  label?: string;
  to?: string;
}

export interface MatchedShellRoute {
  labelKey: string;
  mode: ShellRouteDefinition['mode'];
  parentPath: string | null;
  deleteDomain: DeleteDomain | null;
  detailId: string | null;
  actionIds: ShellActionId[];
  breadcrumbs: ShellBreadcrumb[];
}

const shellRouteDefinitions: ShellRouteDefinition[] = [
  { pattern: '/dashboard', labelKey: 'navigation.dashboard', mode: 'static', actionIds: ['notification', 'delete'] },
  { pattern: '/projects', labelKey: 'navigation.projects', mode: 'collection', deleteDomain: 'projects', actionIds: ['notification', 'create', 'delete'] },
  {
    pattern: '/projects/:projectId',
    labelKey: 'navigation.projects',
    mode: 'editor',
    parentPath: '/projects',
    parentLabelKey: 'navigation.projects',
    detailParam: 'projectId',
    detailFallbackKey: 'navigation.projects',
    deleteDomain: 'projects',
    actionIds: ['sync', 'delete']
  },
  { pattern: '/leads', labelKey: 'navigation.leads', mode: 'collection', deleteDomain: 'leads', actionIds: ['notification', 'create', 'delete'] },
  {
    pattern: '/leads/:leadId',
    labelKey: 'navigation.leads',
    mode: 'editor',
    parentPath: '/leads',
    parentLabelKey: 'navigation.leads',
    detailParam: 'leadId',
    detailFallbackKey: 'navigation.leads',
    deleteDomain: 'leads',
    actionIds: ['sync', 'delete']
  },
  { pattern: '/tasks', labelKey: 'navigation.tasks', mode: 'static', deleteDomain: 'tasks', actionIds: ['notification', 'create', 'delete'] },
  { pattern: '/customers', labelKey: 'navigation.customers', mode: 'collection', deleteDomain: 'customers', actionIds: ['notification', 'create', 'delete'] },
  {
    pattern: '/customers/:customerId',
    labelKey: 'navigation.customers',
    mode: 'editor',
    parentPath: '/customers',
    parentLabelKey: 'navigation.customers',
    detailParam: 'customerId',
    detailFallbackKey: 'navigation.customers',
    deleteDomain: 'customers',
    actionIds: ['sync', 'delete']
  },
  { pattern: '/resources', labelKey: 'navigation.resources', mode: 'collection', actionIds: ['notification', 'create', 'delete'] },
  {
    pattern: '/resources/:assetId',
    labelKey: 'navigation.resources',
    mode: 'editor',
    parentPath: '/resources',
    parentLabelKey: 'navigation.resources',
    detailParam: 'assetId',
    detailFallbackKey: 'navigation.resources',
    deleteDomain: 'resources',
    actionIds: ['sync', 'delete']
  },
  { pattern: '/contracts', labelKey: 'navigation.contracts', mode: 'collection', deleteDomain: 'contracts', actionIds: ['notification', 'create', 'delete'] },
  {
    pattern: '/contracts/templates',
    labelKey: 'contracts.templates.title',
    mode: 'static',
    parentPath: '/contracts',
    parentLabelKey: 'navigation.contracts',
    actionIds: ['notification', 'delete']
  },
  {
    pattern: '/contracts/templates/:templateId',
    labelKey: 'contracts.templates.title',
    mode: 'editor',
    parentPath: '/contracts/templates',
    parentLabelKey: 'contracts.templates.title',
    detailParam: 'templateId',
    detailFallbackKey: 'contracts.templates.newTitle',
    actionIds: ['sync', 'delete']
  },
  {
    pattern: '/contracts/:contractId',
    labelKey: 'navigation.contracts',
    mode: 'editor',
    parentPath: '/contracts',
    parentLabelKey: 'navigation.contracts',
    detailParam: 'contractId',
    detailFallbackKey: 'navigation.contracts',
    deleteDomain: 'contracts',
    actionIds: ['sync', 'delete']
  },
  { pattern: '/invoices', labelKey: 'navigation.invoices', mode: 'collection', deleteDomain: 'invoices', actionIds: ['notification', 'create', 'delete'] },
  {
    pattern: '/invoices/:invoiceId',
    labelKey: 'navigation.invoices',
    mode: 'editor',
    parentPath: '/invoices',
    parentLabelKey: 'navigation.invoices',
    detailParam: 'invoiceId',
    detailFallbackKey: 'navigation.invoices',
    deleteDomain: 'invoices',
    actionIds: ['sync', 'delete']
  },
  { pattern: '/payments', labelKey: 'navigation.payments', mode: 'collection', deleteDomain: 'payments', actionIds: ['notification', 'delete'] },
  {
    pattern: '/payments/:paymentOrderId',
    labelKey: 'navigation.payments',
    mode: 'editor',
    parentPath: '/payments',
    parentLabelKey: 'navigation.payments',
    detailParam: 'paymentOrderId',
    detailFallbackKey: 'navigation.payments',
    deleteDomain: 'payments',
    actionIds: ['sync', 'delete']
  },
  {
    pattern: '/settings/company-profiles',
    labelKey: 'settings.companyProfiles.title',
    mode: 'static',
    parentPath: '/settings',
    parentLabelKey: 'navigation.settings',
    actionIds: ['notification', 'delete']
  },
  {
    pattern: '/settings/customer-types',
    labelKey: 'settings.customerTypes.title',
    mode: 'static',
    parentPath: '/settings',
    parentLabelKey: 'navigation.settings',
    actionIds: ['notification', 'delete']
  },
  {
    pattern: '/settings/payments',
    labelKey: 'settings.groups.payments',
    mode: 'static',
    parentPath: '/settings',
    parentLabelKey: 'navigation.settings',
    actionIds: ['notification', 'delete']
  },
  { pattern: '/settings', labelKey: 'navigation.settings', mode: 'static', actionIds: ['notification', 'delete'] },
  { pattern: '/avi', labelKey: 'navigation.avi', mode: 'static', actionIds: ['notification', 'delete'] },
  { pattern: '/help', labelKey: 'navigation.help', mode: 'static', actionIds: ['notification', 'delete'] }
];

export function getShellRouteMetadata(pathname: string, detailTitle: string | null): MatchedShellRoute {
  for (const definition of shellRouteDefinitions) {
    const match = matchPath({ path: definition.pattern, end: true }, pathname);

    if (!match) {
      continue;
    }

    const detailId = definition.detailParam ? match.params[definition.detailParam] ?? null : null;
    const breadcrumbs: ShellBreadcrumb[] = [];

    if (definition.parentPath && definition.parentLabelKey) {
      breadcrumbs.push({
        labelKey: definition.parentLabelKey,
        to: definition.parentPath
      });
    }

    if (definition.mode === 'editor') {
      if (detailTitle) {
        breadcrumbs.push({ label: detailTitle });
      } else {
        breadcrumbs.push({ labelKey: definition.detailFallbackKey ?? definition.labelKey });
      }
    } else {
      breadcrumbs.push({ labelKey: definition.labelKey });
    }

    return {
      labelKey: definition.labelKey,
      mode: definition.mode,
      parentPath: definition.parentPath ?? null,
      deleteDomain: definition.deleteDomain ?? null,
      detailId,
      actionIds: definition.actionIds,
      breadcrumbs
    };
  }

  return {
    labelKey: 'navigation.dashboard',
    mode: 'static',
    parentPath: null,
    deleteDomain: null,
    detailId: null,
    actionIds: ['notification'],
    breadcrumbs: [{ labelKey: 'navigation.dashboard' }]
  };
}
